import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, map, mergeMap, timeout } from 'rxjs/operators';
import { AppAdapter } from 'src/app/adapters/app-info/app.adapter';
import { MSAppInfo } from 'src/app/adapters/app-info/utils/app.utils';
import { DeviceAdapter } from 'src/app/adapters/device/device.adapter';
import { MSDeviceInfo } from 'src/app/adapters/device/utils/device.utils';
import { EnvironmentAdapter } from 'src/app/adapters/environment/environment.adapter';
import { Environment } from 'src/environments/environment.utils';
import { MSApiCachedGetUseCase } from '../cache/msapi-cached-get.use-case';
import { CheckVersionEvent, mapMSHttpEventToCheckVersionEvent } from './utils/check-version.utils';

/* The CheckVersionUseCase class is a stateful use case that executes an API call to check if the app
is up to date with the most recent version, and returns the response as an API event state. */
@Injectable({
  providedIn: 'root'
})
export class CheckVersionUseCase {
  private argumentsObservable = forkJoin([
    this.environmentAdapter.getEnvironment(),
    this.deviceAdapter.getDeviceInfo(),
    this.appAdapter.getAppInfo()
  ]);

  private callObservable = new Observable((observer) => observer.next()).pipe(
    mergeMap(() => this.argumentsObservable),
    exhaustMap(([environment, deviceInfo, appInfo]) => this.checkVersion(environment, deviceInfo, appInfo)),
    distinctUntilChanged(
      (previous, current) => previous instanceof CheckVersionEvent.Loading && current instanceof CheckVersionEvent.Loading
    )
  );

  constructor(
    private api: MSApiCachedGetUseCase,
    private environmentAdapter: EnvironmentAdapter,
    private deviceAdapter: DeviceAdapter,
    private appAdapter: AppAdapter
  ) {}

  public execute(): Observable<CheckVersionEvent.Spec> {
    return this.callObservable;
  }

  private checkVersion(environment: Environment, deviceInfo: MSDeviceInfo, appInfo: MSAppInfo): Observable<CheckVersionEvent.Spec> {
    const checkVersionObservable = this.api
      .get(environment.distanceConfig.cacheTimeout, 'check_version', {
        appVersion: appInfo.version,
        platform: deviceInfo.platform
      })
      .pipe(
        timeout(environment.distanceConfig.httpRequestTimeout),
        map((event) => mapMSHttpEventToCheckVersionEvent(event)),
        catchError((error) => of(mapMSHttpEventToCheckVersionEvent(error)))
      );

    return checkVersionObservable;
  }
}
